/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    Grid, TextField,
} from '@material-ui/core';
import {
    object, func, string, bool, shape,
} from 'prop-types';

const styles = (theme) => ({
    birth: {
        width: '250px',
        justifyContent: 'center',
    },
    cardTitleBirth: {
        fontSize: '16px',
        fontWeight: '700',
        margin: '15px 0px 8px 0px',
    },
    textField: {
        minHeight: '45px',
        margin: '0px 0px 0px 15px',
        '& .MuiFormHelperText-root': {
            fontSize: '9px',
        },
        '& .MuiInputBase-root': {
            display: 'block',
        },
        '& .MuiInputBase-input': {
            padding: '9px 0',
            textAlign: 'center',
        },
    },
    enterBirthInput: {
        width: '60px',
        height: '40px',
        border: `1px solid ${theme.palette.grey8}`,
        fontSize: '16px',
        marginTop: '3px',
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        '& .MuiInput-underline:before, .MuiInput-underline:after': {
            width: '59px',
        },
        '& .MuiFormHelperText-root': {
            width: '65px',
            margin: '5px 0px',
        },
    },
    enterBirthYearInput: {
        width: '60px',
        height: '38px',
        border: `1px solid ${theme.palette.grey8}`,
        fontSize: '16px',
        marginTop: '3px',
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        '& .MuiFormHelperText-root': {
            margin: '5px 0px',
            width: '85px',
        },
    },
    ageFieldsInput: {
        margin: '2px 2px 0px -12px',
    },
    ageVerification: {
        // keep minimal styles if you need pseudo-selectors for ::after/::before
        position: 'relative',
    },
    label: {
        fontSize: '12px',
        fontWeight: 500,
        marginBottom: '4px',
    },
});

const DateOfBirthContainer = ({
    classes,
    getBirthMonth,
    validateFields,
    birthMonth,
    monthErrorMessage,
    getBirthDay,
    birthDay,
    dayErrorMessage,
    getBirthYear,
    birthYear,
    yearErrorMessage,
    handleAgeVerification,
    inlinePDPAddon,
    label,
    type,
}) => {
    const yearFieldRef = useRef(null);
    const monthFieldRef = useRef(null);
    const dayFieldRef = useRef(null);
    const numericOnly = /^[0-9]+$/;
    const [ageValidation, setAgeValidation] = useState({ flag: false, class: classes.ageInput });

    useEffect(() => {
    // Only run age verification when all fields are filled
        if (birthDay && birthMonth && birthYear) {
            const result = handleAgeVerification(type);
            setAgeValidation(result); // local state, no infinite loop
        } else {
            setAgeValidation({ flag: false, class: classes.ageInput });
        }
    }, [birthDay, birthMonth, birthYear, handleAgeVerification, type]);

    return (
        <div className={classes.birth}>
            <p className={classes.cardTitleBirth}>
                {label}
            </p>
            <Grid container direction="row" className={classes.ageFieldsInput}>
                <div className={`input-field ${classes.textField}`}>
                    <div className={classes.label}>Month</div>
                    <TextField
                        inputRef={monthFieldRef}
                        margin="dense"
                        id="month"
                        type="tel"
                        placeholder="mm"
                        onChange={(e) => {
                            getBirthMonth(e);
                            if (e.target.value.length === 2 && e.target.value <= 12) {
                                if (birthDay.length === 2 && birthDay <= 31) {
                                    yearFieldRef.current.focus();
                                    return;
                                }
                                dayFieldRef.current.focus();
                            }
                        }}
                        className={classes.enterBirthInput}
                        onBlur={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthMonth : e.target.value, id: 'month' } });
                        }}
                        onKeyUp={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthMonth : e.target.value, id: 'month' } });
                        }}
                        value={birthMonth}
                        inputProps={{
                            'data-testid': 'month-field',
                            maxLength: 2,
                            pattern: '[0-9]/g',
                            shrink: true,
                            min: 1,
                            max: 12,
                        }}
                        error={monthErrorMessage !== ''}
                        helperText={!inlinePDPAddon && monthErrorMessage}
                    />
                </div>
                <div className={`input-field ${classes.textField}`}>
                    <div className={classes.label}>Day</div>
                    <TextField
                        margin="dense"
                        id="day"
                        inputRef={dayFieldRef}
                        type="tel"
                        placeholder="dd"
                        onChange={(e) => {
                            getBirthDay(e);
                            if (e.target.value.length === 2 && e.target.value <= 31) {
                                yearFieldRef.current.focus();
                            }
                        }}
                        className={classes.enterBirthInput}
                        onBlur={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthDay : e.target.value, id: 'day' } });
                        }}
                        onKeyUp={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthDay : e.target.value, id: 'day' } });
                        }}
                        value={birthDay}
                        inputProps={{
                            'data-testid': 'day-field',
                            maxLength: 2,
                            pattern: '[0-9]/g',
                            shrink: true,
                            min: 1,
                            max: 31,
                        }}
                        error={dayErrorMessage !== ''}
                        helperText={!inlinePDPAddon && dayErrorMessage}
                    />
                </div>
                <div className={`input-field ${classes.textField}`}>
                    <div className={classes.label}>Year</div>
                    <TextField
                        inputRef={yearFieldRef}
                        margin="dense"
                        id="year"
                        label=""
                        type="tel"
                        placeholder="yyyy"
                        className={classes.enterBirthYearInput}
                        onKeyUp={(event) => {
                            validateFields(event);
                        }}
                        onChange={(event) => {
                            validateFields({ target: { value: !numericOnly.test(event.target.value) ? birthYear : event.target.value, id: 'year' } });
                            getBirthYear(event);
                        }}
                        value={birthYear}
                        inputProps={{
                            maxLength: 4,
                            'data-testid': 'year-field',
                            pattern: '[0-9]/g',
                            shrink: true,
                        }}
                        error={yearErrorMessage !== ''}
                        helperText={!inlinePDPAddon && yearErrorMessage}
                    />
                </div>
                <div disabled={!ageValidation.flag} className={`age-verification ${ageValidation.class}`} />

            </Grid>
        </div>
    );
};

DateOfBirthContainer.defaultProps = {
    inlinePDPAddon: false,
    label: '',
    type: '',
};
DateOfBirthContainer.propTypes = {
    classes: object.isRequired,
    birthDay: string.isRequired,
    birthMonth: string.isRequired,
    birthYear: string.isRequired,
    dayErrorMessage: string.isRequired,
    monthErrorMessage: string.isRequired,
    yearErrorMessage: string.isRequired,
    getBirthDay: func.isRequired,
    getBirthMonth: func.isRequired,
    getBirthYear: func.isRequired,
    handleAgeVerification: func.isRequired,
    validateFields: func.isRequired,
    inlinePDPAddon: bool,
    label: string,
    type: string,
};

export default withStyles(styles)(DateOfBirthContainer);
